import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Button, FormHelperText, Grid, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { get } from 'lodash-es';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { removeCommas } from '../../util/helpers';
import DatePicker from '../forms/DatePicker';
import NumericTextField from '../forms/NumericTextField';
import Select from '../forms/Select';
import TextField from '../forms/TextField';

function CustomBillingOptionsList() {
  const name = 'customBillingOptions';
  const {
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext();
  const {
    fields: customBillingOptions,
    append,
    remove,
  } = useFieldArray({
    name,
  });
  const error = get(errors, name);

  const removeCustomBillingOption = (index) => {
    // If there is an ID, it means it exists in the database,
    // so we must tell the backend to destroy it.
    // Otherwise, it was only added on the UI, and we can safely remove it
    if (getValues(`${name}.${index}.id`)) {
      setValue(`${name}.${index}._destroy`, '1', {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      remove(index);
    }
  };

  const billingOptions = [
    'LCI CPM',
    'LCI Flat Fee',
    'CPA',
    'CPV',
    'DOOH',
    'Social',
    'LiveRamp',
    'Orion',
    'Custom',
    'gIROAS',
    'Tradedesk',
  ].map((item) => ({
    value: item,
    label: item,
  }));

  const defaultNewBillingOptions = { billingOption: billingOptions[0].value };

  const getCustomBillingOptionsCount = () =>
    customBillingOptions.filter((field, index) => !watch(`${name}.${index}._destroy`)).length;

  return (
    <Grid item xs={12}>
      <Stack spacing={2}>
        <Stack direction="row" width={1} justifyContent="space-between">
          <Typography variant="body2" mb={2} color="text.primary">
            Custom Billing Options ({getCustomBillingOptionsCount()})
          </Typography>
          <Button onClick={() => append(defaultNewBillingOptions, { shouldFocus: true })} aria-label="Add New">
            Add New
          </Button>
        </Stack>
        {error && <FormHelperText error>{error.message?.toString()}</FormHelperText>}
        {customBillingOptions.map((field, index) => {
          const startDate = watch(`${name}.${index}.startDate`);
          const endDate = watch(`${name}.${index}.endDate`);
          // eslint-disable-next-line no-underscore-dangle
          if (watch(`${name}.${index}._destroy`)) {
            return null;
          }
          return (
            <Stack key={field.id} direction="row" spacing={1}>
              <Select
                fullWidth
                name={`${name}.${index}.billingOption` as const}
                label="Billing Option"
                options={billingOptions}
              />
              <TextField
                variant="standard"
                size="small"
                name={`${name}.${index}.externalId` as const}
                label="External ID"
                fullWidth
              />
              <TextField
                variant="standard"
                size="small"
                name={`${name}.${index}.reportingName` as const}
                label="Reporting Name"
                fullWidth
              />
              <NumericTextField
                fullWidth
                name={`${name}.${index}.impressionBudget` as const}
                label="Impression Budget"
                allowNegative={false}
                decimalScale={0}
                autoComplete="off"
                thousandSeparator=","
                valueIsNumericString
                normalizeOutput={(e) => removeCommas(e.target.value)}
              />
              <NumericTextField
                fullWidth
                name={`${name}.${index}.rate` as const}
                label="Rate"
                allowNegative={false}
                decimalScale={7}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
                thousandSeparator=","
                valueIsNumericString
                normalizeOutput={(e) => removeCommas(e.target.value)}
              />
              <NumericTextField
                fullWidth
                name={`${name}.${index}.dollarBudget` as const}
                label="Dollar Budget"
                allowNegative={false}
                decimalScale={2}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
                autoComplete="off"
                thousandSeparator=","
                valueIsNumericString
                normalizeOutput={(e) => removeCommas(e.target.value)}
              />
              <TextField fullWidth name={`${name}.${index}.notes` as const} label="Notes" autoComplete="off" />
              <DatePicker
                fullWidth
                name={`${name}.${index}.startDate` as const}
                label="Start Date"
                maxDate={endDate}
                slotProps={{
                  actionBar: {
                    actions: ['clear'],
                  },
                }}
              />
              <DatePicker
                fullWidth
                name={`${name}.${index}.endDate` as const}
                label="End Date"
                minDate={startDate}
                slotProps={{
                  actionBar: {
                    actions: ['clear'],
                  },
                }}
              />
              <IconButton onClick={() => removeCustomBillingOption(index)} aria-label={`Delete Index ${index}`}>
                <DeleteOutlinedIcon />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>
    </Grid>
  );
}

export default CustomBillingOptionsList;
